import React, { useState } from "react";

/**
 * The name the CA types into Zoho's invite form.
 *
 * Fixed rather than free text so every client's books end up with the same
 * recognisable user. Nothing reads it back — membership is confirmed over the
 * API against `expected_org_id` — so this is for whoever later opens Users &
 * Roles and has to work out what that account is.
 */
export const ZOURNAL_USER_NAME = "Zournal System";

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="shrink-0 rounded-md bg-muted px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted/70"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Field({ label, value, copyable }: { label: string; value: string; copyable?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="w-28 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-all font-mono text-[13px]">{value}</span>
      {copyable ? <CopyButton value={value} label={label} /> : null}
    </div>
  );
}

/**
 * What to type into Zoho Books' Invite User form, laid out as that form's own
 * fields.
 *
 * The screen used to hand over the address alone, which left the CA to invent a
 * name and to know that the role has to be Admin. Both matter: a role below
 * Admin connects and then fails verification with "added as X rather than an
 * admin", after the client has already done the work. Mirroring the three
 * fields turns the step into transcription rather than interpretation.
 *
 * Role carries no copy button on purpose — it is a dropdown in Zoho, so there
 * is nothing to paste.
 */
export function ZohoInviteFields({ email }: { email: string }) {
  return (
    <div className="flex flex-col divide-y rounded-md border">
      <Field label="Name" value={ZOURNAL_USER_NAME} copyable />
      <Field label="Email address" value={email} copyable />
      <Field label="Role" value="Admin" />
    </div>
  );
}
